import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import { AddressInput } from '@/components/Form/AddressInput/AddressInput';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';

import { ADDRESS_MAX_LENGTH } from '@/constants/places';

import { useTranslate } from '@/hooks/useTranslate';

import { Option } from '@/types/components/common';

type AddressFormControlProps<TFieldValues extends FieldValues> = Readonly<{
  control: Control<TFieldValues>;
  isPending: boolean;
}>;

const ADDRESS_TEXT_FIELD_CONFIG = {
  city: {
    labelKey: 'createNewAccount.addressPage.form.city.label',
    maximumKey: 'createNewAccount.addressPage.form.city.error.maximum',
    requiredKey: 'createNewAccount.addressPage.form.city.error.required',
    maxLength: ADDRESS_MAX_LENGTH.city,
  },
  state: {
    labelKey: 'createNewAccount.addressPage.form.state.label',
    maximumKey: 'createNewAccount.addressPage.form.state.error.maximum',
    maxLength: ADDRESS_MAX_LENGTH.state,
  },
  zip: {
    labelKey: 'createNewAccount.addressPage.form.zipCode.label',
    maximumKey: 'createNewAccount.addressPage.form.zipCode.error.maximum',
    maxLength: ADDRESS_MAX_LENGTH.zip,
  },
} as const;

type AddressTextFieldKind = keyof typeof ADDRESS_TEXT_FIELD_CONFIG;

function getMaxLengthHelperText(
  valueLength: number,
  maxLength: number,
  message: string,
): string | undefined {
  return valueLength >= maxLength ? message : undefined;
}

function AddressTextField<
  TFieldValues extends FieldValues,
  K extends AddressTextFieldKind,
>({
  kind,
  name,
  control,
  isPending,
  required,
  hideLabel,
}: AddressFormControlProps<TFieldValues> &
  Readonly<{
    kind: K;
    name: Path<TFieldValues>;
    required?: boolean;
    hideLabel?: boolean;
  }>) {
  const { t } = useTranslate();
  const config = ADDRESS_TEXT_FIELD_CONFIG[kind];
  const isRequired = kind === 'city' && required !== false;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required:
          isRequired && 'requiredKey' in config
            ? t(config.requiredKey)
            : undefined,
      }}
      render={({ field: { value, ...field }, fieldState }) => (
        <TextInput<TFieldValues>
          {...field}
          name={name}
          value={typeof value === 'string' ? value : ''}
          error={!!fieldState.error}
          helperText={
            fieldState.error?.message ||
            getMaxLengthHelperText(
              value?.length ?? 0,
              config.maxLength,
              t(config.maximumKey, { value: `${config.maxLength}` }),
            )
          }
          disabled={isPending}
          fullWidth
          label={hideLabel ? '' : t(config.labelKey)}
          inputProps={{
            maxLength: config.maxLength,
            'aria-label': hideLabel ? t(config.labelKey) : undefined,
          }}
        />
      )}
    />
  );
}

export function AddressLine1Field<TFieldValues extends FieldValues>({
  name,
  control,
  isPending,
}: AddressFormControlProps<TFieldValues> &
  Readonly<{ name: Path<TFieldValues> }>) {
  const { t } = useTranslate();

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: t('createNewAccount.addressPage.form.address.error.required'),
      }}
      render={({ field, fieldState }) => (
        <AddressInput<TFieldValues>
          {...field}
          control={control}
          textFieldProps={{
            label: t('createNewAccount.addressPage.form.address.label'),
            error: !!fieldState.error,
            helperText:
              fieldState.error?.message ||
              (typeof field.value === 'string' &&
                getMaxLengthHelperText(
                  field.value.length,
                  ADDRESS_MAX_LENGTH.line1,
                  t('createNewAccount.addressPage.form.address.error.maximum', {
                    value: `${ADDRESS_MAX_LENGTH.line1}`,
                  }),
                )),
            inputProps: {
              maxLength: ADDRESS_MAX_LENGTH.line1,
            },
          }}
          disabled={isPending}
        />
      )}
    />
  );
}

export function AddressCityTextField<TFieldValues extends FieldValues>({
  name,
  control,
  isPending,
  required = true,
  hideLabel = false,
}: AddressFormControlProps<TFieldValues> &
  Readonly<{
    name: Path<TFieldValues>;
    required?: boolean;
    hideLabel?: boolean;
  }>) {
  return (
    <AddressTextField
      kind="city"
      name={name}
      control={control}
      isPending={isPending}
      required={required}
      hideLabel={hideLabel}
    />
  );
}

export function AddressStateField<TFieldValues extends FieldValues>({
  name,
  control,
  isPending,
}: AddressFormControlProps<TFieldValues> &
  Readonly<{ name: Path<TFieldValues> }>) {
  return (
    <AddressTextField
      kind="state"
      name={name}
      control={control}
      isPending={isPending}
    />
  );
}

export function AddressZipField<TFieldValues extends FieldValues>({
  name,
  control,
  isPending,
}: AddressFormControlProps<TFieldValues> &
  Readonly<{ name: Path<TFieldValues> }>) {
  return (
    <AddressTextField
      kind="zip"
      name={name}
      control={control}
      isPending={isPending}
    />
  );
}

type AddressCountrySelectFieldProps<TFieldValues extends FieldValues> =
  AddressFormControlProps<TFieldValues> &
    Readonly<{
      name: Path<TFieldValues>;
      popularCountryOptions: Option[];
      disabled?: boolean;
      onSelectChange?: () => void;
    }>;

export function AddressCountrySelectField<TFieldValues extends FieldValues>({
  name,
  control,
  isPending,
  popularCountryOptions,
  disabled,
  onSelectChange,
}: AddressCountrySelectFieldProps<TFieldValues>) {
  const { t } = useTranslate();

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: 'Country is required',
      }}
      render={({ field, fieldState }) => (
        <Select
          {...field}
          control={control}
          options={popularCountryOptions}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          fullWidth
          disabled={disabled ?? isPending}
          placeholder={t(
            'createNewAccount.addressPage.form.country.placeHolder',
          )}
          label={t('createNewAccount.addressPage.form.country.label')}
          onSelectChange={onSelectChange}
          useDrawerOnMobile
          withSearchBar
        />
      )}
    />
  );
}
